import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment, { Moment } from 'moment'
import { isEmpty } from 'underscore'
import { ADVANCED_ANALYTICS, IPlotVariable, IBestDataset } from '../../../../types/analytics'
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart'
import { IEnrichedMetadata } from '../../../../types/metadata'
import { DT0Format } from '../../../../utils/StaticVariable'
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../types/home'

interface UseExceedanceParams {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  onDatasetChange: (item: string) => void
  dateRange: [moment.Moment, moment.Moment]
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  exceedanceValuesImage: string
  exceedanceValuesLinks: string[]
  error: string
  updating: boolean
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  point?: MarkerItem
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
}

export const useExceedanceParams = (params: UseExceedanceParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    onDatasetChange,
    dateRange,
    enrichedMetadata,
    onUpdate,
    exceedanceValuesImage,
    pointName,
    userInput,
    updateUserInput,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
    error
  } = params

  const selfTriggered = useRef(false)

  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([])
  const [dataset, setDataset] = useState(userInput.Exceedance.dataset)
  const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.Exceedance.variable)
  const [startDate, setStartDate] = useState(userInput.Exceedance.startDate || moment(dateRange[0]))
  const [endDate, setEndDate] = useState(userInput.Exceedance.endDate || moment(dateRange[1]))

  const getImage = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string,
    ) => {
      const [longitude, latitude] = coordinates
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.EXCEEDANCE,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        }
        onUpdate(payload)
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  )

  const onUpdateExceedance = useCallback(
    (dateRange?: any) => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === coordinates.join()
        )

        if (task) {
          getImage(
            variableX,
            task.OrderId,
            dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
            dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format)
          )
        }
      }
    },
    [enrichedMetadata, dataset, startDate, endDate, variableX, pointName]
  )

  const doVariableChange = useCallback((active: number) => {
    const item = listVariablesX.find((e) => e.Index === active)
    setVariableX(item!)
  }, [listVariablesX])

  const doStartDateChange = useCallback((value: any) => {
    if (value) {
      setStartDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([value, endDate])
      }
    }
  }, [endDate, isForAllAnalytics, setGlobalDateRange])

  const doEndDateChange = useCallback((value: any) => {
    if (value) {
      setEndDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([startDate, value])
      }
    }
  }, [startDate, isForAllAnalytics, setGlobalDateRange])

  const onSelectDataset = useCallback((value: string) => {
    onDatasetChange(value)
  }, [onDatasetChange])

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics)
    selfTriggered.current = true
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics])

  const disabledStartDate = useCallback((current: Moment) => {
    const isAfterEndDate = current && current > endDate
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day')
  }, [endDate, dateRange])

  const disabledEndDate = useCallback((current: Moment) => {
    const isBeforeStartDate = current && current < startDate
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day')
  }, [startDate, dateRange])

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label)
    return Array.from(new Set(label))
  }, [datasets])

  useEffect(() => {
    updateUserInput({
      Exceedance: {
        dataset: dataset,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    })
  }, [dataset, variableX, startDate, endDate])

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === coordinates.join()
      )
      if (task) {
        const itemsInfos = task.ItemsInfo
        if (itemsInfos.length > 0) {
          setListVariableX(itemsInfos)
          const indexX = itemsInfos[0]
          if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && exceedanceValuesImage === '' && error === '') {
            let dateRange = [startDate, endDate]
            if (isForAllAnalytics) {
              dateRange = globalDateRange
              setStartDate(dateRange[0])
              setEndDate(dateRange[1])
            }
            setDataset(previewDatasetId)
            setVariableX(indexX)
            selfTriggered.current = true
            getImage(
              indexX,
              task.OrderId,
              dateRange[0].format(DT0Format),
              dateRange[1].format(DT0Format)
            )
          }
        }
      }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, exceedanceValuesImage, error])

  useEffect(() => {
    if (isForAllAnalytics && exceedanceValuesImage !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false
        return
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateExceedance(globalDateRange)
      }
      setStartDate(globalDateRange[0])
      setEndDate(globalDateRange[1])
    }
  }, [isForAllAnalytics, exceedanceValuesImage])

  return {
    listVariablesX,
    dataset,
    variableX,
    startDate,
    endDate,
    categories,
    onUpdateExceedance,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  }
}
